import React from "react";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import PropTypes from "prop-types";
import { Line } from "react-chartjs-2";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import { useMemo } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import configs from "examples/Charts/LineCharts/ReportsLineChart/configs";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  ChartDataLabels // 👈 register plugin
);

const splitTimestampParts = (value = "") => {
  if (!value) return [null, null];
  const normalized = String(value).trim();
  if (!normalized) return [null, null];

  if (normalized.includes("\n")) {
    const [first, ...rest] = normalized
      .split("\n")
      .map((part) => part.trim())
      .filter(Boolean);
    return [first || null, rest.join(" ") || null];
  }

  const isoMatch = normalized.match(/^(\d{4}-\d{2}-\d{2})(?:[T\s](.*))?$/);
  if (isoMatch) {
    return [isoMatch[1], isoMatch[2] ? isoMatch[2].replace(/Z$/, "").trim() || null : null];
  }

  const separators = [" | ", " - ", " � "];
  for (const separator of separators) {
    const index = normalized.indexOf(separator);
    if (index !== -1) {
      const first = normalized.slice(0, index).trim();
      const second = normalized.slice(index + separator.length).trim();
      return [first || normalized, second || null];
    }
  }

  const parts = normalized.split(/\s+/);
  if (parts.length > 1) {
    return [parts[0], parts.slice(1).join(" ") || null];
  }

  return [normalized, null];
};

function MortalityChart({
  color,
  title,
  chart,
  chartHeight,
  mortality_today,
  mortality_target,
  mortality_rate,
  mortality_status,
  mortality_message,
  timestamp,
  live_count,
}) {
  const chartConfig = useMemo(
    () => configs(chart?.labels || [], chart?.datasets || {}),
    [chart?.labels, chart?.datasets]
  );

  const chartData = useMemo(() => {
    const baseData = chartConfig?.data ?? {};
    const datasets = Array.isArray(baseData.datasets) ? baseData.datasets : [];
    if (!datasets.length) {
      return baseData;
    }

    return {
      ...baseData,
      datasets: datasets.map((dataset) => {
        const datasetDatalabels = dataset.datalabels || {};
        return {
          ...dataset,
          datalabels: {
            ...datasetDatalabels,
            display: datasetDatalabels.display !== undefined ? datasetDatalabels.display : true,
            align: "top",
            anchor: "end",
            offset: datasetDatalabels.offset !== undefined ? datasetDatalabels.offset : -1,
          },
        };
      }),
    };
  }, [chartConfig]);

  const chartOptions = useMemo(() => {
    const baseOptions = chartConfig?.options ?? {};
    const basePlugins = baseOptions.plugins ?? {};
    const baseDatalabels = basePlugins.datalabels ?? {};
    const baseScales = baseOptions.scales ?? {};
    const baseXTicks = baseScales.x?.ticks ?? {};

    return {
      ...baseOptions,
      plugins: {
        ...basePlugins,
        datalabels: {
          ...baseDatalabels,
          display:
            baseDatalabels.display !== undefined
              ? baseDatalabels.display
              : (context) => {
                  const datasetDisplay = context?.dataset?.datalabels?.display;
                  if (typeof datasetDisplay === "function") return datasetDisplay(context);
                  if (datasetDisplay !== undefined) return datasetDisplay;
                  return true;
                },
          clamp: baseDatalabels.clamp ?? true,
          color: baseDatalabels.color ?? "#ffffff",
          formatter:
            baseDatalabels.formatter ||
            ((value, context) => {
              const datasetFormatter = context?.dataset?.datalabels?.formatter;
              if (typeof datasetFormatter === "function") {
                return datasetFormatter(value, context);
              }
              if (value === null || value === undefined) return "";
              if (typeof value === "number") {
                return Number.isFinite(value) ? value.toLocaleString() : "";
              }
              return `${value}`;
            }),
        },
      },
      scales: {
        ...baseScales,
        x: {
          ...baseScales.x,
          ticks: {
            ...baseXTicks,
          },
        },
      },
    };
  }, [chartConfig]);

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox padding="1rem">
        {useMemo(
          () => (
            <MDBox
              variant="gradient"
              bgColor={color}
              borderRadius="lg"
              coloredShadow={color}
              py={2}
              pr={0.5}
              mt={-5}
              height={chartHeight}
            >
              <Line data={chartData} options={chartOptions} />
            </MDBox>
          ),
          [chartData, chartOptions, color, chartHeight]
        )}
        <MDBox pt={3} pb={1} px={1}>
          <MDTypography variant="h6" textTransform="capitalize">
            {title}
          </MDTypography>
          <MDTypography variant="h6" textTransform="capitalize">
            Live Birds: {live_count}
          </MDTypography>

          <MDBox
            position="absolute"
            bottom={45}
            right={16} // You can adjust this value for exact horizontal spacing
            display="flex"
            flexDirection="column"
            alignItems="flex-start"
            gap={0}
          >
            {mortality_today != null ? (
              <>
                <MDBox display="flex" alignItems="center">
                  <MDTypography sx={{ fontSize: "1rem", minWidth: "55px" }} color="textSecondary">
                    Today:
                  </MDTypography>

                  <MDBox
                    component="span"
                    sx={{
                      px: 1.2,
                      py: 0.2,
                      mx: 0.5,
                      borderRadius: "4px",
                      backgroundColor: (theme) => {
                        switch ((mortality_status || "").toLowerCase()) {
                          case "green":
                            return "success.main";
                          case "amber":
                            return "warning.main";
                          case "red":
                            return "error.main";
                          default:
                            return theme.palette.grey[400];
                        }
                      },
                      color: (theme) => theme.palette.common.white,
                      fontWeight: "bold",
                      fontSize: "1rem",
                    }}
                  >
                    {mortality_today}
                  </MDBox>

                  {mortality_rate != null && (
                    <MDTypography sx={{ fontSize: "1rem" }} color="textSecondary">
                      ({mortality_rate})
                    </MDTypography>
                  )}
                </MDBox>

                <MDBox display="flex" alignItems="center">
                  <MDTypography sx={{ fontSize: "1rem", minWidth: "55px" }} color="textSecondary">
                    Target: {mortality_target}
                  </MDTypography>
                </MDBox>
              </>
            ) : (
              <MDBox
                display="flex"
                alignItems="center"
                sx={{ justifyContent: "flex-end", width: "100%" }}
              >
                <MDBox
                  sx={{
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    maxWidth: { xs: "calc(100% - 120px)", sm: "200px" },
                    position: "relative",
                    ml: { xs: -2, sm: 0.5 },
                  }}
                >
                  <MDTypography
                    sx={{
                      display: "inline-block",
                      fontSize: "1rem",
                      color: "text.error",
                      animation: "scroll-left 10s linear infinite",
                    }}
                  >
                    {mortality_message}
                  </MDTypography>

                  <style>
                    {`
                      @keyframes scroll-left {
                        0% { transform: translateX(100%); }
                        100% { transform: translateX(-100%); }
                      }
                    `}
                  </style>
                </MDBox>
              </MDBox>
            )}
          </MDBox>

          <MDBox
            position="absolute"
            bottom={8}
            left={16} // You can adjust this value for exact horizontal spacing
            display="flex"
            alignItems="center"
          >
            <MDTypography variant="button" color="text" lineHeight={1} sx={{ mt: 0.15, mr: 0.5 }}>
              <Icon>schedule</Icon>
            </MDTypography>
            <MDTypography variant="button" color="text" fontWeight="light">
              {timestamp}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of ReportsLineChart
MortalityChart.defaultProps = {
  color: "info",
  description: "",
  canvasHeight: "12.5rem",
};

// Typechecking props for the ReportsLineChart
MortalityChart.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  title: PropTypes.string,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  chart: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.array, PropTypes.object])).isRequired,
  chartHeight: PropTypes.string,
  mortality_today: PropTypes.number,
  mortality_target: PropTypes.number,
  mortality_rate: PropTypes.number,
  mortality_status: PropTypes.string,
  mortality_message: PropTypes.string,
  live_count: PropTypes.number,
  timestamp: PropTypes.string,
};

export default MortalityChart;
